import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Copy, Check, RefreshCw, LayoutGrid } from 'lucide-react';
import { toast } from '@clareefai/ui';
import type {
  ReviewsWidgetType,
  ReviewsWidgetSelectionMode,
  ReviewsWidgetOrder,
  UpdateReviewsWidgetConfigRequest,
  TestimonialResponse,
} from '@clareefai/types';
import {
  useGetReviewsWidgetConfigQuery,
  useUpdateReviewsWidgetConfigMutation,
  useGetAllTestimonialsQuery,
} from '../../store/vendorApi';
import ReviewsWidgetPreview, { PreviewReview } from '../../components/widget/ReviewsWidgetPreview';
import SelectReviewsModal from '../../components/widget/SelectReviewsModal';

const WIDGET_TYPES: ReviewsWidgetType[] = ['carousel', 'cards', 'grid', 'list'];
const ORDERS: ReviewsWidgetOrder[] = ['newest', 'featured', 'random'];

const toPreview = (r: TestimonialResponse): PreviewReview => ({
  id: r.id,
  promoterName: r.promoterName,
  promoterCompany: r.promoterCompany,
  rating: r.rating,
  comment: r.content,
  vendorResponse: r.vendorResponse ?? null,
});

// Shown in the preview when the current config yields no real reviews, so the
// vendor can still see the layout/styling before publishing or selecting any.
const SAMPLE_REVIEWS: PreviewReview[] = [
  {
    id: 'sample-1',
    promoterName: 'Michel Dubois',
    promoterCompany: 'Digital Agency',
    rating: 5,
    comment:
      'Excellent solution that truly transformed the way we work. The team is very professional and communication was always exceptional.',
    vendorResponse: 'Thank you, Michel — it has been a pleasure working with you!',
  },
  {
    id: 'sample-2',
    promoterName: 'Sarah Chen',
    promoterCompany: 'Acme SaaS',
    rating: 5,
    comment: 'Onboarding was smooth and the results spoke for themselves within weeks. Highly recommend.',
    vendorResponse: null,
  },
  {
    id: 'sample-3',
    promoterName: 'Tom Becker',
    promoterCompany: 'Northwind',
    rating: 4,
    comment: 'Solid product with responsive support. A few features we would love to see, but overall great value.',
    vendorResponse: null,
  },
];
const SAMPLE_AVERAGE = 4.7;
const SAMPLE_TOTAL = 127;

const ReviewsWidgetSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: config, isLoading } = useGetReviewsWidgetConfigQuery();
  // Publicly-displayable pool — same set the widget renders: REVIEW + VIDEO that
  // are PUBLISHED or FEATURED. The grouped endpoint returns both buckets at once
  // (a testimonial sits in exactly one bucket, so the two are disjoint).
  const { data: testimonials } = useGetAllTestimonialsQuery({});
  const [updateConfig, { isLoading: isSaving }] = useUpdateReviewsWidgetConfigMutation();
  const [copied, setCopied] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Local edits override the saved config (no effect needed — mirrors WidgetSettings).
  const [widgetType, setWidgetType] = useState<ReviewsWidgetType | null>(null);
  const [selectionMode, setSelectionMode] = useState<ReviewsWidgetSelectionMode | null>(null);
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[] | null>(null);
  // undefined = untouched (null is a real value meaning "all ratings").
  const [minRating, setMinRating] = useState<number | null | undefined>(undefined);
  const [maxReviews, setMaxReviews] = useState<number | null>(null);
  const [order, setOrder] = useState<ReviewsWidgetOrder | null>(null);
  const [showCompanyResponse, setShowCompanyResponse] = useState<boolean | null>(null);
  const [heading, setHeading] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState<string | null>(null);
  const [ctaLabel, setCtaLabel] = useState<string | null>(null);

  const eligible = [...(testimonials?.published ?? []), ...(testimonials?.featured ?? [])].filter(
    (r) => r.type === 'REVIEW' || r.type === 'VIDEO',
  );

  if (isLoading || !config) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const eType = widgetType ?? config.widgetType ?? 'carousel';
  const eMode = selectionMode ?? config.selectionMode ?? 'filter';
  const eSelectedIds = selectedReviewIds ?? config.selectedReviewIds ?? [];
  const eMinRating = minRating === undefined ? config.minRating ?? null : minRating;
  const eMaxReviews = maxReviews ?? config.maxReviews ?? 3;
  const eOrder = order ?? config.order ?? 'newest';
  const eShowResponse = showCompanyResponse ?? config.showCompanyResponse ?? true;
  const eHeading = heading ?? config.heading ?? 'What our customers say';
  const eAccent = accentColor ?? config.accentColor ?? '#4F46E5';
  const eCtaLabel = ctaLabel ?? config.ctaLabel ?? '';

  // Aggregate stats over the full published pool (matches the public payload).
  const ratings = eligible.map((r) => r.rating ?? 0).filter((n) => n > 0);
  const averageRating = ratings.length ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 100) / 100 : 0;

  // Which reviews the preview shows — mirrors PublicReviewsWidgetService.selectReviews.
  const previewSource =
    eMode === 'manual'
      ? eSelectedIds
          .map((id) => eligible.find((r) => r.id === id))
          .filter((r): r is TestimonialResponse => Boolean(r))
      : (() => {
          const filtered = eligible.filter((r) => eMinRating == null || (r.rating ?? 0) >= eMinRating);
          const ordered =
            eOrder === 'featured'
              ? [...filtered].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured))
              : eOrder === 'random'
                ? filtered
                : [...filtered].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
          return ordered;
        })();
  const previewReviews = previewSource.slice(0, Math.max(1, eMaxReviews)).map(toPreview);

  // Fall back to sample data so the preview is never blank (e.g. before any
  // reviews are published, or in manual mode with nothing selected yet).
  const usingSample = previewReviews.length === 0;
  const displayReviews = usingSample
    ? SAMPLE_REVIEWS.slice(0, Math.max(1, Math.min(eMaxReviews, SAMPLE_REVIEWS.length)))
    : previewReviews;
  const displayAverage = usingSample ? SAMPLE_AVERAGE : averageRating;
  const displayTotal = usingSample ? SAMPLE_TOTAL : eligible.length;

  const save = async (overrides: UpdateReviewsWidgetConfigRequest = {}) => {
    try {
      await updateConfig({
        widgetType: eType,
        selectionMode: eMode,
        selectedReviewIds: eSelectedIds,
        minRating: eMinRating,
        maxReviews: eMaxReviews,
        order: eOrder,
        showCompanyResponse: eShowResponse,
        heading: eHeading,
        accentColor: eAccent,
        ctaLabel: eCtaLabel,
        ...overrides,
      }).unwrap();
      toast.success(t('reviewsWidget.saved', 'Reviews widget saved'));
    } catch {
      toast.error(t('reviewsWidget.saveError', 'Could not save. Please try again.'));
    }
  };

  const copySnippet = async () => {
    if (!config.snippet) return;
    await navigator.clipboard.writeText(config.snippet);
    setCopied(true);
    toast.success(t('reviewsWidget.snippetCopied', 'Embed code copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const copyToken = async () => {
    if (!config.publicKey) return;
    await navigator.clipboard.writeText(config.publicKey);
    setCopiedToken(true);
    toast.success(t('reviewsWidget.tokenCopied', 'Token copied'));
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const conversion = config.views > 0 ? Math.round((config.leads / config.views) * 1000) / 10 : 0;

  const fieldClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{t('reviewsWidget.enable', 'Enable reviews widget')}</p>
            <p className="text-sm text-gray-600">
              {config.isEnabled
                ? t('reviewsWidget.enabledHint', 'Your reviews widget is live on your site.')
                : t('reviewsWidget.disabledHint', 'Turn on to generate your embed key and snippet.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => save({ isEnabled: !config.isEnabled })}
          disabled={isSaving}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
            config.isEnabled ? 'bg-purple-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {config.isEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Configuration column */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
            <h2 className="font-semibold text-gray-900">{t('reviewsWidget.configuration', 'Widget Configuration')}</h2>

            {/* Widget type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.widgetType', 'Widget Type')}</label>
              <select value={eType} onChange={(e) => setWidgetType(e.target.value as ReviewsWidgetType)} className={fieldClass}>
                {WIDGET_TYPES.map((v) => (
                  <option key={v} value={v}>{t(`reviewsWidget.type.${v}`, v)}</option>
                ))}
              </select>
            </div>

            {/* Source + count */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.selectionMode', 'Review Source')}</label>
                <select value={eMode} onChange={(e) => setSelectionMode(e.target.value as ReviewsWidgetSelectionMode)} className={fieldClass}>
                  <option value="filter">{t('reviewsWidget.mode.filter', 'Automatic (by filter)')}</option>
                  <option value="manual">{t('reviewsWidget.mode.manual', 'Hand-picked')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.numberOfReviews', 'Number of Reviews')}</label>
                <select value={eMaxReviews} onChange={(e) => setMaxReviews(Number(e.target.value))} className={fieldClass}>
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {eMode === 'manual' ? (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200"
              >
                {t('reviewsWidget.selectReviewsCount', 'Select Reviews ({{count}})', { count: eSelectedIds.length })}
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.filterByRating', 'Filter by Ratings')}</label>
                  <select
                    value={eMinRating == null ? '' : String(eMinRating)}
                    onChange={(e) => setMinRating(e.target.value === '' ? null : Number(e.target.value))}
                    className={fieldClass}
                  >
                    <option value="">{t('reviewsWidget.allRatings', 'All ratings')}</option>
                    {[4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>{t('reviewsWidget.starsPlus', '{{count}}+ stars', { count: n })}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.order', 'Order')}</label>
                  <select value={eOrder} onChange={(e) => setOrder(e.target.value as ReviewsWidgetOrder)} className={fieldClass}>
                    {ORDERS.map((o) => (
                      <option key={o} value={o}>{t(`reviewsWidget.orderBy.${o}`, o)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Show company response */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.showCompanyResponse', 'Show Company Response')}</label>
              <select
                value={eShowResponse ? 'yes' : 'no'}
                onChange={(e) => setShowCompanyResponse(e.target.value === 'yes')}
                className={fieldClass}
              >
                <option value="yes">{t('reviewsWidget.yes', 'Yes')}</option>
                <option value="no">{t('reviewsWidget.no', 'No')}</option>
              </select>
            </div>

            {/* Heading + accent */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.headingLabel', 'Heading')}</label>
                <input type="text" value={eHeading} onChange={(e) => setHeading(e.target.value)} className={fieldClass} />
              </div>
              <input
                type="color"
                value={eAccent}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-10 w-14 rounded border border-gray-300 flex-shrink-0"
                title={t('reviewsWidget.accentColor', 'Accent color') as string}
              />
            </div>

            {/* CTA — clicking it opens the built-in lead form (same as the public page). */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviewsWidget.cta', 'Call-to-Action Button')}</label>
              <input
                type="text"
                value={eCtaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder={t('reviewsWidget.ctaLabelPlaceholder', 'Request a Call') as string}
                className={fieldClass}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('reviewsWidget.ctaHint', 'Visitors who click this fill out a short form — submissions appear in your Demo Requests.')}
              </p>
            </div>

            <button
              onClick={() => save()}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('reviewsWidget.save', 'Save')}
            </button>
          </div>

          {/* Preview + embed + analytics column */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">{t('reviewsWidget.preview', 'Widget Preview')}</h2>
                {usingSample && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                    {t('reviewsWidget.sampleData', 'Sample data')}
                  </span>
                )}
              </div>
              {usingSample && (
                <p className="text-xs text-gray-500">
                  {t(
                    'reviewsWidget.sampleHint',
                    'Showing example reviews — publish or select reviews to preview your real ones.',
                  )}
                </p>
              )}
              <ReviewsWidgetPreview
                widgetType={eType}
                heading={eHeading}
                accentColor={eAccent}
                showCompanyResponse={eShowResponse}
                ctaLabel={eCtaLabel || null}
                reviews={displayReviews}
                averageRating={displayAverage}
                totalReviews={displayTotal}
                emptyLabel={t('reviewsWidget.previewEmpty', 'Publish some reviews to preview your widget.')}
              />
            </div>

            {/* Token + embed code */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">{t('reviewsWidget.embedCode', 'Embed Code')}</h2>
                <button
                  onClick={() => save({ regenerateKey: true })}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-60"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('reviewsWidget.regenerate', 'Regenerate key')}
                </button>
              </div>

              {/* Widget token (shown separately for programmatic use) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('reviewsWidget.token', 'Widget token')}
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={config.publicKey ?? ''}
                    onFocus={(e) => e.currentTarget.select()}
                    className={`${fieldClass} font-mono text-xs bg-gray-50`}
                  />
                  <button
                    onClick={copyToken}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex-shrink-0"
                  >
                    {copiedToken ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedToken ? t('reviewsWidget.copied', 'Copied') : t('reviewsWidget.copy', 'Copy')}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t(
                    'reviewsWidget.tokenHint',
                    'Public identifier for this widget. Safe to expose; regenerate to invalidate existing embeds.',
                  )}
                </p>
              </div>

              {/* Full snippet */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('reviewsWidget.snippet', 'Embed snippet')}
                </label>
                <p className="text-sm text-gray-600 mb-2">
                  {t('reviewsWidget.snippetHint', 'Paste this into your website where you want the reviews to appear.')}
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all">
                    {config.snippet}
                  </pre>
                  <button
                    onClick={copySnippet}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? t('reviewsWidget.copied', 'Copied') : t('reviewsWidget.copyCode', 'Copy Code')}
                  </button>
                </div>
              </div>
            </div>

            {/* Analytics */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <h2 className="font-semibold text-gray-900">{t('reviewsWidget.analytics', 'Widget Analytics')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-blue-50 p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{config.views.toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('reviewsWidget.views', 'Widget Views')}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">{config.ctaClicks.toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('reviewsWidget.ctaClicks', 'CTA Clicks')}</p>
                </div>
                <div className="rounded-lg bg-green-50 p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{config.leads.toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('reviewsWidget.callsRequested', 'Calls Requested')}</p>
                </div>
                <div className="rounded-lg bg-purple-50 p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">{conversion}%</p>
                  <p className="text-xs text-gray-600 mt-1">{t('reviewsWidget.conversion', 'Conversion Rate')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <SelectReviewsModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        reviews={eligible}
        selectedIds={eSelectedIds}
        onChange={(ids) => setSelectedReviewIds(ids)}
      />
    </div>
  );
};

export default ReviewsWidgetSettings;
